import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import MessageModal, {
  type MessageModalProps,
} from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import { CancelButton } from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons';
import { Leave as LeaveImage } from '@wix/da-ds/pkg/Icons/Confirmations';
import { popAllModals } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import PromptCountAndPointsBalance from './PromptCountAndPointsBalance';
import { useSpendType } from './useSpendType';
import SpendConfirmationButton from './SpendConfirmationButton';
import type { SpendConfirmationActionType } from './useSpendConfirmationAction';
import { useSpendConfirmationMessage } from './useSpendConfirmationMessage';

import s from './SpendConfirmationModal.scss';

interface Props
  extends Omit<
    MessageModalProps,
    | 'title'
    | 'message'
    | 'image'
    | 'footerButtons'
    | 'variant'
    | 'doNotShowAgainText'
    | 'onDoNotShowAgainChange'
    | 'isOpen'
  > {
  deviation: PapiDeviation;
  confirmationActionType: SpendConfirmationActionType;
  biDataComponent: string;
}

const SpendConfirmationModal: React.FC<Props> = ({
  deviation,
  confirmationActionType,
  onRequestClose,
  biDataComponent,
  ...rest
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const spendType = useSpendType();
  const dispatchPopAllModals = useAction(popAllModals);
  const message = useSpendConfirmationMessage(confirmationActionType);

  return (
    <MessageModal
      isOpen
      onRequestClose={onRequestClose}
      className={s['root']}
      image={<LeaveImage />}
      variant={isMobile ? 'vertical' : 'horizontal'}
      title={t('dreamup.modal.spendConfirmation.title')}
      message={
        <div className={s['content-root']}>
          {message}
          {'\n\n'}
          <PromptCountAndPointsBalance spendType={spendType} />
        </div>
      }
      footerButtons={[
        <CancelButton key="cancel" onClick={onRequestClose}>
          {t('dreamup.modal.spendConfirmation.cancelButton')}
        </CancelButton>,
        <SpendConfirmationButton
          key="confirm"
          confirmationActionType={confirmationActionType}
          spendType={spendType}
          deviation={deviation}
          biDataComponent={biDataComponent}
          // This modal can be stacked on top of the `DreamupModals.PROMPT_INFO` modal, so
          // we pop all of them on confirmation to be sure
          closeCallback={dispatchPopAllModals}
        />,
      ]}
      {...rest}
    />
  );
};

SpendConfirmationModal.displayName = 'SpendConfirmationModal';

export default SpendConfirmationModal;
